<template>
  <collapse-portlet :ref="self_id" :title="heading"
                    :expanded="expanded"
                    :icon="icon" :locked="locked"
                    :inline="locked"
                    :show-heading="showHeading">
    <template slot="portlet_actions">
      <div class="f-data-table-table-actions">
        <slot name="_actions"
              :do="(options) => executeActionCb(options)"
              :reload-data="() => fetchData(true)"
              :rows="parsed_rows" />
      </div>
    </template>
    <!-- GENERAL TOOLS -->
    <slot name="_tools" :reload-data="() => fetchData(true)" />
    <!-- FILTERS FORM -->
    <slot name="_filters" :reload-data="() => fetchData(true)" />
    <!-- TABLE CONTAINER -->
    <div class="kt-portlet__body kt-portlet__body--fit">
      <div class="kt-datatable kt-datatable--default kt-datatable--brand kt-datatable--loaded">
        <template v-if="!loading">
          <!-- ROW SELECTION CONTROLS -->
          <template v-if="parsed_rows.some(row => row.selected)">
            <div class="f-data-table-clear-row-select">
              <label class="kt-font-bold kt-font-danger-">Selected {{ parsed_rows.filter(row => row.selected).length }} records:</label>
              <a href="javascript:void(0)" @click="clearSelectedRows()">[ Clear selection ]</a>
            </div>

            <div class="f-data-table-row-controls-container kt-form__group kt-form__group--inline">
              <div class="f-data-table-row-controls kt-form__control">
                <div class="btn-toolbar">
                  <slot name="_row_controls"
                        :do="(options) => executeActionCb(options)" />
                </div>
              </div>
            </div>
          </template>
          <!-- TABLE ITSELF -->
          <table v-if="!no_data && !errored"
                 class="f-data-table kt-datatable__table">
            <colgroup>
              <col v-if="unfittable_columns.length > 0" :style="{ width: `${dropArrowColWidth}px`}">
              <col v-if="selectableRows" class="f-data-table-select-col-header" :style="{ 'width': `${selectColWidth}px` }">
              <col v-for="(column, index) in fittable_columns"
                   :key="index"
                   :style="{
                     'width': `${column.calc_width}px`,
                   }">
            </colgroup>
            <thead class="f-data-table-head kt-datatable__head">
              <tr class="f-data-table-row kt-datatable__row">
                <th v-if="unfittable_columns.length > 0" class="f-data-table-coldrop-control" />
                <th v-if="selectableRows" class="f-data-table-select-col-header">
                  <ss-checkbox :id="`${id}-row-select-all`" @change="ev => selectRows(...parsed_rows.map(row => ({ row, selected: ev })))" />
                </th>
                <th v-for="(column, index) in fittable_columns"
                    :key="index"
                    class="f-data-table-cell kt-datatable__cell kt-datatable__cell--sort"
                    :style="{
                      cursor: column.sortable ? `pointer` : `default`
                    }"
                    :class="{
                      'kt-datatable__cell--sorted': column.active_sort
                    }"
                    @click="() => {
                      if (column.sortable) sortByCol(column);
                    }">
                  <span class="f-data-table-col-head-content" :title="column.sortable ? `Sort by ${column.display}` : ``">
                    <span class="f-data-table-col-head-display" :class="{
                      'active-sort': column.active_sort,
                    }">{{ column.display }}</span>
                    <template v-if="column.sortable">
                      <span class="f-data-table-col-head-control">
                        <span v-if="column.active_sort" class="active-sort">{{ [[`desc`, `▼`], [`asc`, `▲`]].find(dir => dir[0] === column.sort_dir)[1] }}</span>
                        <i v-else class="flaticon2-sort" />
                      </span>
                    </template>
                    <!-- COLUMN TOGGLE BUTTON (always on last col) -->
                    <template v-if="index === fittable_columns.length - 1 && toggleable_cols.length">
                      <div class="btn btn-icon btn-ss-link" style="margin-left: auto; font-size: 1.6em"
                           @click="showColumnsModal">
                        <i class="la la-cog" style="font-size: 1em" />
                      </div>
                    </template>
                  </span>
                </th>
                <template v-if="autoCompleteColumns">
                  <th v-for="(upc, upc_index) in unnamed_pod_columns"
                      :key="upc_index"
                      class="f-data-table-cell kt-datatable__cell"
                      :style="{
                        lineBreak: `${autoCompleteColumns ? `anywhere` : `normal`}`
                      }">
                    {{ upc.display }}
                  </th>
                </template>
              </tr>
            </thead>
            <tbody class="f-data-table-body kt-datatable__body">
              <template v-for="(row, index) in parsed_rows">
                <tr :key="row.uuid"
                    class="f-data-table-row kt-datatable__row"
                    :class="{
                      'f-data-table-row-selected': row.selected
                    }">
                  <td v-if="unfittable_columns.length > 0"
                      class="f-data-table-coldrop-control"
                      @click="expandRow(index)">
                    {{ row.expanded ? `▼` : `▶` }}
                  </td>
                  <td v-if="selectableRows" class="f-data-table-select-row" :style="{ width: `${selectColWidth}px`}">
                    <ss-checkbox :id="`${id}-row-select-${index}`" v-model="row.selected" @change="ev => selectRows({ row, selected: ev })" />
                  </td>
                  <td v-for="col in fittable_columns"
                      :key="col.key_id"
                      class="f-data-table-cell kt-datatable__cell"
                      :style="{
                        ...cellStyles,
                      }">
                    <slot v-if="$scopedSlots[col.field]"
                          :field="col.field"
                          :name="col.field"
                          :row="row.data"
                          :value="row.data[col.field]"
                          :index="index"
                          :do="(options) => executeActionCb({...options, target: [row.data]})" />
                    <template v-else>
                      <template v-if="typeof col.renderHtml === `function`">
                        <span v-sanitise="col.renderHtml(row.data)" />
                      </template>
                      <span v-else>{{ row.data[col.field] }}</span>
                    </template>
                  </td>
                  <template v-if="autoCompleteColumns">
                    <td v-for="(upc, upc_index) in unnamed_pod_columns"
                        :key="upc_index"
                        class="f-data-table-cell kt-datatable__cell"
                        :style="{
                          lineBreak: `${autoCompleteColumns ? `anywhere` : `normal`}`,
                          ...cellStyles,
                        }">
                      <span>{{ row.data[upc.field] }}</span>
                    </td>
                  </template>
                </tr>
                <tr v-if="row.expanded" :key="`secondary-${index}`">
                  <td colspan="100" class="kt-datatable__detail">
                    <table class="w-100 kt-datatable__table">
                      <tr v-for="(col, uf_index) in unfittable_columns" :key="uf_index" class="kt-datatable__row">
                        <th class="kt-datatable__cell" style="width: 20%;">
                          {{ col.display }}
                        </th>
                        <td class="kt-datatable__cell">
                          <slot v-if="$scopedSlots[col.field]"
                                :field="col.field"
                                :name="col.field"
                                :row="row.data"
                                :value="row.data[col.field]"
                                :index="index"
                                :do="({ cb, target, reload_data }) => executeActionCb(cb, target || [row.data], reload_data)" />
                          <span v-else>{{ row.data[col.field] }}</span>
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
          <div v-else-if="no_data" class="no-data-message">
            No data to display!
          </div>
          <div v-else-if="errored" class="errored-message">
            <p class="errored-alert">
              Error fetching data!
            </p>
            <template v-if="rows.error">
              <p>Server responsed with:</p>
              <div>
                <p class="error-itself">
                  <span v-if="rows.error.status">{{ rows.error.status }}:</span> {{ rows.error.message }}
                </p>
              </div>
            </template>
            <button class="btn btn-ss" @click="() => fetchData(true)">
              Refresh
            </button>
          </div>
          <pagination-controls v-if="pagination.enabled && rows"
                               :initial-page="live_pagination.default_page"
                               :max-page-size="live_pagination.per_page"
                               :total-rows="serverSide === false ? filterData(rows.data).length : rows.paginator.total"
                               :page-count="live_pagination.page_count"
                               :current-slice="{
                                 start: paginate_from,
                                 end: paginate_to
                               }"
                               @pagination-page-change="updatePaginationPage"
                               @pagination-perpage-change="updatePaginationPerPage" />
        </template>
        <template v-else>
          <div v-if="!$scopedSlots['_loading']" class="no-data-message">
            <loader loading-text="Loading..." />
          </div>
          <template v-else>
            <slot name="_loading" />
          </template>
        </template>
      </div>
    </div>
    <f-data-table-cols-modal :table-id="id"
                             :columns="toggleable_cols"
                             @toggle="setColumnVisible" />
  </collapse-portlet>
  <!-- </div> -->
</template>

<script>
import ldDebounce from "lodash/debounce";
import { nullish, fallback } from 'Lib/type-sanity';
import CollapsePortlet from "VueComponents/CollapsePortlet.vue";
import PaginationControls from 'VueComponents/PaginationControls.vue';
import FDataTableColsModal from 'VueModals/FDataTableColsModal';
import Loader from "VuePartials/Loader";

const find_closest_displayed_parent = (element) => {
  const display_style = window.getComputedStyle(element).display;
  if (display_style !== `none` && element.clientWidth !== 0 && element.clientHeight !== 0) {
    return element;
  } else {
    return find_closest_displayed_parent(element.parentElement);
  }
};

// assign meta information such as sorting attributes, etc.
const construct_column = (body, field) => {
  const out_col = {
    ...body,
  };
  if (field) {
    out_col.field = field;
  } else {
    out_col.field = body.field;
  }
  out_col.key_id = Symbol(out_col.field);
  // whether the column is being displayed or not (controlled via the checkboxes)
  if (nullish(out_col.visible)) {
    out_col.visible = true;
  }
  if (nullish(out_col.toggleable)) {
    out_col.toggleable = false;
  }
  // " col is sortable
  if (nullish(out_col.sortable)) {
    out_col.sortable = true;
  }
  // the column header text - user-visible references to the column use this text
  if (nullish(out_col.display)) {
    out_col.display = out_col.field;
  }
  // the initial sort direction
  if (nullish(out_col.sort_dir)) {
    out_col.sort_dir = `desc`;
  }
  // column won't shrink beyond this
  if (nullish(out_col.min_width)) {
    out_col.min_width = out_col.width;
  }
  // higher clip resistance = more resistant to being clipped into dropdown on table resize
  if (nullish(out_col.clip_resistance)) {
    out_col.clip_resistance = 0;
  }
  return out_col;
};

const only_first_key = (obj_arr, key, value, reset_value) => {
  const first = obj_arr.find(obj => obj[key] === value);
  if (first !== undefined) {
    obj_arr.filter(obj => obj !== first).map(obj => obj[key] = reset_value);
  }
  return obj_arr;
};

const filter_by_ops = (filter_val, ...ops) => {
  const valid_ops = [
    `ct`,
    `gt`,
    `lt`,
    `eq`,
    `in`,
    `bt`,
    `gte`,
    `lte`,
    `sw`,
    `ew`,
  ];
  if (!ops.every(op => typeof op === `function` || valid_ops.includes(op.toLowerCase()))) {
    throw new Error(`Operator is not recognized, must of one of: ${valid_ops.join(`, `)}, or a callback.`);
  } else {
    return ops.map((op) => {
      if (typeof op === `function`) {
        return op;
      } else {
        switch (op) {
          case `in`:
            return comparison => comparison.includes(filter_val);
          case `bt`:
            return comparison => comparison[0] < filter_val && comparison[1] > filter_val;
          case `sw`:
            return comparison => comparison.toString().toLowerCase().startsWith(filter_val.toString().toLowerCase());
          case `ew`:
            return comparison => comparison.toString().endsWith(filter_val.toString().toLowerCase());
          case `lt`:
            return comparison => comparison < filter_val;
          case `lte`:
            return comparison => comparison <= filter_val;
          case `gt`:
            return comparison => comparison > filter_val;
          case `gte`:
            return comparison => comparison >= filter_val;
          case `eq`:
            return comparison => comparison === filter_val;
          case `ct`:
          default:
            return comparison => comparison.toString().toLowerCase().includes(filter_val.toString().toLowerCase());
        }
      }
    });
  }
};

export default {
  components: {
    CollapsePortlet,
    PaginationControls,
    FDataTableColsModal,
    Loader,
  },
  props: {
    id: {
      required: false,
      type: String,
      default: 'fdt',
    },
    data: {
      required: false,
      type: Array,
      default: () => ([]),
    },
    dataUri: {
      required: false,
      type: String,
      default: null,
    },
    staticParams: {
      required: false,
      type: Array,
      default: () => ([]),
    },
    preFilter: {
      required: false,
      type: Array,
      default: () => ([]),
    },
    serverSide: {
      required: false,
      type: Boolean,
      default: false,
    },
    heading: {
      required: false,
      type: String,
      default: ``,
    },
    fixedHeight: {
      required: false,
      type: Number,
      default: undefined,
    },
    columns: {
      required: true,
      type: Array,
      default: () => ([{
        field: `uuid`,
        display: `UUID`,
        sort_dir: `desc`,
      }]),
    },
    cellStyles: {
      required: false,
      type: Object,
      default: () => ({}),
    },
    autoCompleteColumns: {
      required: false,
      type: Boolean,
      default: false,
    },
    autoHideColumns: {
      required: false,
      type: Boolean,
      default: false,
    },
    sorting: {
      required: false,
      type: Object,
      default: () => ({
        enabled: true,
        sort_whole_set: true,
      }),
      validator: (options) => {
        if (!options || typeof options !== 'object') {
          return false;
        }
        if (options.enabled === undefined) {
          options.enabled = true;
        } else if (typeof options.enabled !== 'boolean') {
          return false;
        }
        if (options.sort_whole_set === undefined) {
          options.sort_whole_set = true;
        } else if (typeof options.sort_whole_set !== 'boolean') {
          return false;
        }
        return true;
      },
    },
    pagination: {
      required: false,
      type: Object,
      default: () => ({
        enabled: true,
        per_page: 25,
        default_page: 1,
      }),
    },
    filtering: {
      required: false,
      type: Object,
      default: () => ({
        enabled: true,
      }),
      validator: (options) => {
        if (!options || typeof options !== `object`) {
          return false;
        }
        if (typeof options.enabled !== `boolean`) {
          return false;
        }
        return true;
      },
    },
    selectableRows: {
      required: false,
      type: Boolean,
      default: false,
    },
    dropArrowColWidth: {
      required: false,
      type: Number,
      default: 30,
    },
    selectColWidth: {
      required: false,
      type: Number,
      default: 40,
    },
    expanded: {
      required: false,
      type: Boolean,
      default: true,
    },
    icon: {
      type: String,
      required: false,
      default: `flaticon2-gear`,
    },
    locked: {
      type: Boolean,
      required: false,
      default: false,
    },
    showHeading: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  data() {
    return {
      rows: [],
      first_load: true,
      loading: true,
      errored: false,
      no_data: false,
      live_pagination: {
        enabled: true,
        per_page: 5,
        default_page: 1,
        ...this.pagination,
        current_page: this.pagination.default_page || 1,
      },
      // convert plain string cols to objects, also fill out minimal fields if not present
      parsed_columns: (() => {
        const semi_parsed = only_first_key(this.columns.map(col => construct_column(col)), `active_sort`, true, false);
        return semi_parsed;
      })(),
      filters: [],
      // used after mount to calc column widths, needs refactoring out of data at some point
      self_id: Math.ceil(Math.random() * 100 * Date.now()),
      parsed_rows: [],
      fittable_columns: [],
      pre_filter: this.preFilter,
    };
  },
  computed: {
    toggled_cols() {
      return this.toggleable_cols.filter(col => col.visible === false);
    },
    toggleable_cols() {
      return this.parsed_columns.filter(col => col.toggleable);
    },
    self_element() {
      return nullish(this.$refs[this.self_id]) ? undefined : this.$refs[this.self_id].$el;
    },
    unfittable_columns() {
      return this.parsed_columns.filter(col => !this.fittable_columns.map(col => col.field).includes(col.field) && col.visible);
    },
    paginate_from() {
      if (this.errored) return 0;
      const pgn = this.live_pagination;
      return this.serverSide ? this.rows.paginator.from || 0 : Math.max(0, Math.min((pgn.current_page * pgn.per_page) - pgn.per_page, this.rows.data.length - 1));
    },
    paginate_to() {
      if (this.errored) return 0;
      return this.serverSide ? this.rows.paginator.to || 0 : this.paginate_from + this.live_pagination.per_page;
    },
    // named valueD columns are columns which were explicitly provided in the columns object (named),
    // and also valued in slots
    named_valued_columns() {
      return !this.errored && this.rows && this.rows.data && this.rows.data.length ? this.parsed_columns
        .filter(col => Object.keys(this.$scopedSlots).includes(col.field)
        || Object.keys(this.rows.data[0]).includes(col.field)
        && col.visible)
        : [];
    },
    // if autoCompleteColumns is true, we have a computed prop to call which will return all
    // - Unnamed
    // - Plain Old Data (fundamental types not including object, function, symbol or undefined)
    // columns which we can iterate through and display.
    unnamed_pod_columns() {
      return Object.keys(this.rows.data[0])
        .filter(k => !this.parsed_columns.map(col => col.field).includes(k))
        .filter(k => ![
          'object',
          'function',
          'symbol',
          'undefined',
        ].includes(typeof this.rows.data[0][k])).map((col, index, arr) => ({
          field: col,
          display: col,
          width: `${100 / (arr.length + this.named_valued_columns.length)}%`,
        }));
    },
  },
  // on mount, we need to insert the columns which were defined as slot templates but not explicitly set in the columns object
  // we also need to unset loading when data is loaded, then set up pagination based on the row count and the pagination options
  // finally, we sort the rows by the default sorting columns (there is only one no matter what)
  mounted() {
    this.rows = { data: this.data }; // initial set from props

    this.tableResize = ldDebounce(() => this.calcFittableColumns(), 200);

    window.EventBus.$on(`fdt-filter-change`, (event) => {
      if (event.id !== this.id) return;

      this.live_pagination.current_page = 1;
      const incoming = {
        name: event.name,
        ...(() => {
          if (this.serverSide) {
            return event.server;
          } else {
            return event.client;
          }
        })(),
      };
      const cur_filt = this.filters.findIndex(f => f.name === event.name);
      if (this.filters[cur_filt]) {
        this.filters.splice(cur_filt, 1, incoming);
      } else {
        this.filters.push(incoming);
      }
      const cur_pre_filt = this.pre_filter.findIndex(f => f.field === event.name);
      if (this.pre_filter[cur_pre_filt]) {
        this.pre_filter.splice(cur_pre_filt, 1);
      }
      this.fetchData(this.serverSide);
    });

    window.EventBus.$on(`${this.id ?? `fdt`}-reload`, () => {
      this.fetchData(true);
    });

    window.EventBus.$on(`${this.id ?? `fdt`}-update-row`, (ev) => {
      const row_index = this.parsed_rows.findIndex(pr => ev.find(pr.data));
      if (row_index >= 0) {
        const payload = {
          ...this.parsed_rows[row_index],
          data: {
            ...this.parsed_rows[row_index].data,
            [ev.field]: ev.value,
          },
        };
        this.parsed_rows.splice(row_index, 1, payload);
      }
    });

    // insert slot cols
    const reserved_slots = [
      `default`,
      `_actions`,
      `_tools`,
      `_filters`,
      `_row_controls`,
    ];

    // parsed columns: merging of currently defined, add columns for the scoped slots if they arent included already
    this.parsed_columns = [
      ...this.parsed_columns,
      ...Object.entries(this.$scopedSlots)
        .filter(slot_col => !reserved_slots.includes(slot_col[0]))
        .filter(slot_col => !this.parsed_columns
          .map(col => col.field)
          .includes(slot_col[0]))
        .map(slot_col => construct_column(slot_col[1], slot_col[0])),
    ];

    for (const [k, v] of [...new URLSearchParams(location.search)]) {
      switch (k) {
        case `p`: // pagination page
          this.live_pagination.current_page = parseInt(v);
          this.live_pagination.default_page = parseInt(v);
          break;
        case `f`: // filters
          this.filters = [
            ...this.filters,
            ...v.split(`,`).map((filt_str) => {
              const [field, op, value] = filt_str.split(`:`);
              return {
                field,
                op,
                value,
                type: `scalar`,
              };
            }),
          ];
          break;
      }
    }

    this.initializeTable();
    window.addEventListener(`resize`, this.tableResize);
  },
  destroyed() {
    window.removeEventListener(`resize`, this.tableResize);
  },
  methods: {
    showColumnsModal() {
      this.fireModal("fdt-cols-modal");
    },
    setColumnVisible({ col, visible }) {
      this.parsed_columns.find(p_col => p_col.field === col.field).visible = visible;
      this.calcFittableColumns();

      const cookie_data = this.$cookies.get(this.id) ?? {};
      if (nullish(cookie_data.hidden_cols)) {
        cookie_data.hidden_cols = [];
      }
      if (visible) {
        cookie_data.hidden_cols.splice(cookie_data.hidden_cols.findIndex(h_col => h_col.field === col.field), 1);
      } else {
        cookie_data.hidden_cols.push(col.field);
      }
      this.$cookies.set(this.id, cookie_data, `1y`);
    },
    initializeTable() {
      // on data load...
      this.fetchData(true).then(() => {
        if (!this.errored) {
        // set up data vars
          if (this.rows.data.length) {
            // then sort the rows if possible
            this.calcFittableColumns();
            this.loading = false;
          } else {
            this.loading = false;
            this.no_data = true;
          }
        }
      });
      // column visibility cookie fetch
      const hidden_cols = this.$cookies.get(this.id)?.hidden_cols;
      if (!nullish(hidden_cols)) {
        for (const field of hidden_cols) {
          this.parsed_columns.find(col => col.field === field).visible = false;
        }
      }
    },
    clearSelectedRows() {
      this.parsed_rows = this.parsed_rows.map(row => ({
        ...row,
        selected: false,
      }));
      [
        ...document.querySelectorAll(`.f-data-table input[type=checkbox]`),
      ].map(cb_el => cb_el.checked = false);
    },
    deleteRow(row, key) {
      axios.delete(`${this.dataUri}/${row[key]}`);
      this.rows.data = this.rows.data.filter(r => r[key] !== row[key]);
      this.fetchData();
    },
    selectRows(...selected_rows) {
      this.parsed_rows
        .filter(row => selected_rows.map(s_row => s_row.row).includes(row))
        .map((row) => {
          row.selected = selected_rows.find(s_row => s_row.row === row).selected;
          return row;
        });
      EventBus.$emit(`fdt-rows-selected`, this.parsed_rows.filter(row => row.selected).map(row => row.data));
    },
    expandRow(index) {
      this.parsed_rows[index].expanded = !this.parsed_rows[index].expanded;
    },
    calcFittableColumns() {
      if (!this.self_element) {
        this.fittable_columns = [];
        return;
      }
      // first, get the width of the table or its closest set-width parent
      const client_width = (find_closest_displayed_parent(this.self_element).clientWidth - this.selectColWidth - this.dropArrowColWidth);
      const set_width_cols = this.parsed_columns.filter(col => col.width && col.visible);
      const unset_width_cols = this.parsed_columns.filter(col => !col.width && col.visible);
      // now, split the unused width evenly between remaining cols
      const leftover_width = Math.floor(Math.max(100, (client_width - set_width_cols.reduce((acc, col) => acc + col.width, 0)) / unset_width_cols.length));
      this.parsed_columns = _.uniqWith(this.parsed_columns, _.isEqual).map(col => ({
        ...col,
        calc_width: fallback(col.width, leftover_width),
      }));
      // clone and sort by worst -> best
      const columns_to_fit = this.parsed_columns.filter(col => col.visible).slice().sort((a, b) => {
        // by never_clip
        if (a.never_clip && !b.never_clip) return -1;
        else if (!a.never_clip && b.never_clip) return 1;
        // then by clip resistance
        if (a.clip_resistance > b.clip_resistance) return -1;
        else if (a.clip_resistance < b.clip_resistance) return 1;
        // then by width (smaller is 'worse')
        if (a.calc_width < b.calc_width) return -1;
        else if (a.calc_width > b.calc_width) return 1;
        // otherwise, equal
        return 0;
      });
      // now, add columns to the list of fittables by the order they're sorted into
      // if they are possible to fit in
      const is_fittable = (state, col) => {
        const is_over_threshold = col => col.clip_resistance >= state.last_failed_resistance;
        const is_slim_enough = col => col.calc_width + state.used_width <= client_width;
        return is_over_threshold(col) && is_slim_enough(col);
      };

      const fittables = columns_to_fit.reduce((acc, col) => {
        if (col.never_clip === true) {
          acc.used_width += col.calc_width;
          acc.cols.push(col);
        } else if (is_fittable(acc, col)) {
          acc.used_width += col.calc_width;
          acc.cols.push(col);
        } else {
          acc.last_failed_resistance = col.clip_resistance;
        }
        return acc;
      }, { used_width: 0, last_failed_resistance: -Infinity, cols: [] }).cols;
      // now, distribute any remaining width to these fittables
      const remaining_width = Math.max(0, (client_width - fittables.reduce((acc, col) => acc + col.calc_width, 0)) / fittables.length);
      const final_cut = fittables.map(col => ({
        ...col,
        calc_width: col.width ? col.width : col.calc_width + remaining_width,
      }));

      // finally, sort this list by the index they originally had, and assign it to fittable_columns
      this.fittable_columns = final_cut.sort((a, b) => {
        const index_a = this.parsed_columns.findIndex(col => col.field === a.field);
        const index_b = this.parsed_columns.findIndex(col => col.field === b.field);

        if (index_a < index_b) return -1;
        else if (index_a > index_b) return 1;
        else return 0;
      });
      this.parsed_columns.map(col => col.key_id = Symbol(col.field));
    },
    async executeActionCb(options = {}) {
      options = {
        // eslint-disable-next-line
        cb: () => {},
        target: this.parsed_rows.filter(row => row.selected).map(row => row.data),
        cancel_value: false,
        reload_data: true,
        ...options,
      };
      const res = await options.cb(...options.target);
      if (options.reload_data && res !== options.cancel_value) {
        this.fetchData(options.reload_data);
        this.clearSelectedRows();
      }
    },
    /**
     * Returns the table's data. Optionally reloading it from the data source (`.dataUri`) if `reload_data` is `true`.
     *
     * @param {boolean} reload_data Reloads the table's data.
     *
     * @return unknown[]
     */
    async fetchData(reload_data = false) {
      if (this.errored || this.no_data) {
        this.errored = false;
        this.no_data = false;
        this.loading = true;
        return this.initializeTable();
      }
      const sort_column = this.parsed_columns.find(col => col.active_sort);
      // `rows`; this is the raw data we receive from the `.dataUri`
      // -> we need to build the full query string which can contain filters etc.:
      const rows = await (async () => {
        // There are several potential parts to the table's query:
        //
        // - `additional_params`: <key>=<value> pairs, which can change per request (i.e param filters like 'orphaned=exclude')
        // - `sort_string`: backend API sort param (https://timbre.local/docs/1.0/filters-and-sorting#sorting)
        // - `filter_string`: backend API filter param (https://timbre.local/docs/1.0/filters-and-sorting#introduction)
        // - `paginate_string`: backend API pagination str (https://timbre.local/docs/1.0/filters-and-sorting#sorting)
        // - `static_additionals`: passed via a prop, these are <key>=<value> pairs which are always applied every query
        //
        // `additional_params` and `static_additionals` are kept seperate for quick readability when sanity checking queries
        const static_additionals = this.staticParams.length ? `${this.staticParams.filter(ap => !nullish(ap.field)).map(ap => `${ap.field}=${ap.value}`).join(`&`)}` : ``;
        if (this.serverSide) {
          this.loading = true;
          const active_filters = [
            ...this.pre_filter,
            ...this.filters,
          ].filter(f => f.value !== undefined && f.value !== null && f.value.length !== 0);
          const param_filters = active_filters.filter(f => f.type === `param`);
          const scalar_filters = active_filters.filter(f => f.type === `scalar`);

          const additional_params = param_filters.length && this.filtering.enabled ? `${param_filters.map(f => `${f.field}=${f.value}`).join(`&`)}` : ``;
          const sort_string = sort_column && this.sorting.enabled ? `sort=${sort_column.field}|${sort_column.sort_dir}` : ``;
          const paginate_string = this.pagination.enabled ? `page=${this.live_pagination.current_page}&per_page=${this.live_pagination.per_page}` : ``;
          const filter_string = scalar_filters.length && this.filtering.enabled ? `filters=${scalar_filters.map(f => `${f.field}:${f.op}:${f.value}`).join(`,`)}` : ``;

          // collect into an array...
          const query_parts = [
            additional_params,
            sort_string,
            filter_string,
            paginate_string,
            static_additionals,
          ];

          // query string = <data uri>?<parts0>&<parts1>&<parts2>...&<partsN>, sans any empty (.length === 0) parts
          const query_string = `${this.dataUri}?${
            query_parts.filter(part => part.length).reduce((out_str, part, index) => `${out_str}${index === 0 ? '' : '&'}${part}`)
          }`;
          // console.log(query_parts);
          // console.log(query_string);
          return (await axios.get(query_string)).data;
        } else if (reload_data && this.dataUri) {
          this.loading = true;
          return (await axios.get(`${this.dataUri}?${static_additionals}`)).data;
        } else {
          return this.rows;
        }
      })();
      this.rows = rows;
      // no data or an error, tell the user something's up and return what we got:
      if (!rows.data || rows.error !== undefined) {
        this.first_load = false;
        this.loading = false;
        this.errored = true;
        return this.rows;
      }
      let new_data = [];
      if (rows.data) {
        // If doing stuff server-side, we skip all these
        // otherwise, we need to _potentially_ do all these things in a specific order, which can vary
        const maybe_filter = data => this.filtering.enabled ? this.filterData(data) : data;
        const maybe_sort = data => this.sorting.enabled && sort_column ? this.sortData(data, sort_column) : data;
        const maybe_paginate = data => this.live_pagination.enabled ? this.paginateData(data) : data;
        const sp_in_order = data => !this.sorting.sort_whole_set ? maybe_sort(maybe_paginate(data)) : maybe_paginate(maybe_sort(data));
        // new_data set
        new_data = this.serverSide ? rows.data : sp_in_order(maybe_filter(rows.data));

        // if server-side, we need to also update the pagination options such as max pages
        // otherwise, calculate it ourselves
        if (this.serverSide) {
          this.refreshPaginationOptionsFromServer(this.rows.paginator);
        } else {
          this.live_pagination.page_count = Math.ceil(this.filterData(this.rows.data).length / this.live_pagination.per_page);
        }
      }
      // new_data is now parsed, adding additional metadata for the table to use (such as `.selected` to tell whether the row is selected)
      // if these metadata fields already, exist, then only update the data for that row:
      new_data = new_data.map((row) => {
        const old_parsed_row = this.parsed_rows.find(parsed_row => parsed_row.data == row);
        if (old_parsed_row) {
          return {
            ...old_parsed_row,
            data: row,
          };
        } else {
          return {
            expanded: false,
            selected: false,
            data: row,
          };
        }
      });
      // store the final result in `.parsed_rows`
      this.parsed_rows = new_data;
      // and announce it:
      EventBus.$emit(`fdt-data-loaded`, {
        rows: this.rows.data,
        parsed_rows: this.parsed_rows,
        first_load: this.first_load,
        id: this.id,
      });
      this.loading = false;
      this.first_load = false;
      if (this.parsed_rows.length === 0) {
        this.no_data = true;
      }
      return this.parsed_rows;
    },
    setColAsSorter(column) {
      this.parsed_columns.map(col => col.active_sort = false);
      column.active_sort = true;
      return column;
    },
    setSorterCol(column) {
      // swap sort dir...
      column.sort_dir = [
        `asc`,
        `desc`,
      ].find(dir => dir !== column.sort_dir);
      // set as active..
      column.active_sort = true;
      return column;
    },
    sortByCol(column) {
      // set all to false...
      this.parsed_columns = this.parsed_columns.map(pc => ({
        ...pc,
        active_sort: false,
      }));
      // set target to true...
      this.parsed_columns[this.parsed_columns.findIndex(pc => pc.field === column.field)] = this.setSorterCol(column);
      this.fetchData();
      this.calcFittableColumns();
    },
    paginateData(data) {
      const from = this.paginate_from;
      const to = this.paginate_to;
      return data.slice(from, to);
    },
    filterData(data) {
      if (!this.errored) {
        const filts = [
          ...this.filters,
          ...(() => {
            if (this.first_load) {
              return this.pre_filter;
            } else {
              return [];
            }
          })(),
        ];
        return data
          .filter(row => filts
            .every(filter => filter_by_ops(filter.value, filter.op).every(comparitor => comparitor(filter.field ? row[filter.field] : row))));
      }
      return [];
    },
    refreshPaginationOptionsFromServer(paginator_details) {
      this.live_pagination.page_count = paginator_details.total_pages;
      if (this.live_pagination.current_page > this.live_pagination.page_count) {
        this.live_pagination.current_page = this.live_pagination.page_count;
      }
    },
    updatePaginationPage(desired_page) {
      if (this.serverSide) this.live_pagination.default_page = desired_page;
      this.live_pagination.current_page = Math.max(1, Math.min(desired_page, this.serverSide ? this.rows.paginator.total_pages : this.live_pagination.page_count));
      this.fetchData();
      this.clearSelectedRows();
      window.EventBus.$emit(`fdt-pagination`, {
        id: this.id,
        pagination: this.live_pagination,
      });
    },
    updatePaginationPerPage(val) {
      this.live_pagination.per_page = Math.max(1, Math.min(val, this.serverSide ? this.rows.paginator.total : this.rows.data.length));
      this.live_pagination.page_count = this.serverSide ? this.rows.paginator.total_pages : Math.ceil(this.rows.data.length / this.live_pagination.per_page);
      if (this.serverSide) {
        this.fetchData();
      }
      this.updatePaginationPage(this.live_pagination.current_page);
    },
    sortData(data, sorter_col) {
      const field = sorter_col.field;
      const sort_func = (() => {
        if (typeof sorter_col.sort_callback === `function`) {
          return sorter_col.sort_callback;
        }
        return (a, b) => {
          if (a[field] < b[field]) return 1;
          else if (a[field] > b[field]) return -1;
          else return 0;
        };
      })();
      const direction = sorter_col.sort_dir;
      if (this.sorting.enabled) {
        // multiply by -1 if desc order:
        return data.sort((a, b) => sort_func(a, b) * ((direction === `asc`) ? -1 : 1));
      } else {
        return data;
      }
    },
  },
};
</script>

<style lang="scss">
@use "sass:color";
@use "sass:map";

@use "AssetsSass/_variables" as *;

.f-data-table-header {
  position: sticky;
  top: 0;
}
.f-data-table-table-actions {
  display: flex;
  flex-direction: row;
  .btn {
    margin: 10px 3px;
  }
}
.f-data-table-row-controls-container {
  padding: 16px 18px 10px 18px;
  display: flex;
  justify-content: space-between;
  position: sticky;
  top: 64px;
  z-index: 10;
  background-color: #fff;
  @media screen and (max-width: 1024px) {
    top: 50px;
  }
  .f-data-table-clear-row-select {
    display: flex;
    flex-direction: column;
  }
  .f-data-table-row-controls {
    button {
      margin: 5px 5px 5px 0;
    }
  }
}
.no-data-message,
.errored-message {
  width: 100%;
  text-align: center;
  margin: 20px 0;
}

.no-data-message {
  font-weight: bold;
  font-size: 1.2em;
}

.errored-message {
  .errored-alert {
    font-weight: bold;
    font-size: 1.2em;
  }
  .error-itself {
    padding: 10px;
    background-color: color.adjust(map.get($ss, 'primary'), $alpha: -0.9, $space: hsl);
    display: inline-block;
    border: 1px solid map.get($ss, 'primary');
    border-radius: 4px;
  }
}
.f-data-table {
  display: block !important;
  border-top: 1px solid rgb(246, 250, 255);
  overflow: visible !important;

  .f-data-table-head {
    display: table-header-group !important;
    .f-data-table-cell {
      .active-sort {
        color: map.get($ss, 'secondary') !important;
        font-weight: 700;
      }
      .f-data-table-col-head-content {
        display: flex !important;
        flex-direction: row;
        justify-content: flex-start;
        align-items:center;
        flex-wrap: nowrap;
        .f-data-table-col-head-display {
          white-space: break-spaces;
        }
        .f-data-table-col-head-control {
          margin-left: 6px;
        }
      }
    }
  }

  .f-data-table-body {
    display: table-row-group !important;
  }

  .f-data-table-row-selected {
    background-color: rgb(206, 240, 252);
  }

  .f-data-table-head {
    display: table-header-group !important;
  }

  .f-data-table-body {
    display: table-row-group !important;
  }

  .f-data-table-row-selected {
    background-color: rgb(206, 240, 252);
  }

 .f-data-table-row {
  left: 0px;
  display: table-row !important;

  .f-data-table-coldrop-control {
    width: 2em;
    text-align: center;
    vertical-align: middle;
  }

  *[class^='f-data-table-select'] {
    border-right: 1px solid rgb(246, 250, 255);
    text-align: center;
    vertical-align: middle;
  }

  .f-data-table-cell {
   border-right: 1px solid rgb(246, 250, 255);
   &:last-child {
    border-right: none;
   }
  }
 }
}
</style>
